import { useState } from 'react';
import { Lock, Sparkles, X } from 'lucide-react';
import { usePermissions } from '../hooks/usePermissions';

/**
 * Card premium pour afficher les fonctionnalités disponibles de manière attrayante
 * 
 * @param {object} props
 * @param {string} props.commercantId - ID du commerçant
 * @param {string} props.permission - Permission requise
 * @param {string} props.title - Titre de la fonctionnalité
 * @param {string} props.icon - Icône (emoji ou composant)
 * @param {string} props.description - Description
 * @param {array} props.features - Liste des fonctionnalités
 * @param {React.ReactNode} props.children - Contenu à afficher si activé
 */
const PremiumFeatureCard = ({
  commercantId,
  permission,
  title,
  icon,
  description,
  features = [],
  children
}) => {
  const { hasPermission, loading, error } = usePermissions(commercantId);
  const [showModal, setShowModal] = useState(false);

  const hasAccess = !loading && (hasPermission(permission) || !permission);

  // Ne jamais démonter le contenu métier pendant le chargement / une erreur réseau :
  // un F5 laissait autrement la zone principale vide (liste jamais montée).
  if (loading || error || hasAccess || !permission) {
    return <>{children}</>;
  }

  // Sinon, afficher la card premium
  return (
    <>
      <div
        className="relative bg-gradient-to-br from-yellow-50 via-white to-blue-50 rounded-xl p-6 border-2 border-yellow-300 shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer group overflow-hidden"
        onClick={() => setShowModal(true)}
      >
        {/* Badge "Option Premium" */}
        <div className="absolute top-3 right-3 flex items-center gap-1 px-3 py-1 bg-gradient-to-r from-yellow-400 to-yellow-600 text-white text-xs font-semibold rounded-full shadow-md">
          <Sparkles className="h-3 w-3" />
          <span>Option Premium</span>
        </div>

        {/* Contenu */}
        <div className="pr-24">
          <div className="flex items-center gap-3 mb-3">
            <div className="text-4xl">{icon}</div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">{title}</h3>
              <p className="text-sm text-gray-600 mt-1">{description}</p>
            </div>
          </div>

          {features.length > 0 && (
            <div className="mt-4">
              <p className="text-xs font-medium text-gray-500 mb-2">✨ Fonctionnalités :</p>
              <ul className="text-sm text-gray-700 space-y-1">
                {features.map((feature, idx) => (
                  <li key={idx} className="flex items-center gap-2">
                    <span className="text-yellow-500">•</span>
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* CTA - Sorti du conteneur pr-24 pour centrer la barre */}
        <div className="mt-5 -mx-6 px-6 pt-4 border-t border-yellow-200 overflow-hidden">
          <button className="w-full bg-gradient-to-r from-yellow-400 to-yellow-600 text-white font-medium py-2.5 rounded-lg hover:from-yellow-500 hover:to-yellow-700 transition-all duration-200 flex items-center justify-center gap-2">
            <Lock className="h-4 w-4" />
            <span>Débloquer cette option</span>
          </button>
        </div>
      </div>

      {/* Modal d'aperçu */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-gradient-to-r from-yellow-400 to-yellow-600 px-6 py-4 flex items-center justify-between rounded-t-xl">
              <div className="flex items-center gap-3">
                <div className="text-3xl">{icon}</div>
                <h3 className="text-xl font-bold text-white">{title}</h3>
              </div>
              <button
                onClick={() => setShowModal(false)}
                className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-1 transition"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6">
              <p className="text-gray-700 mb-4">{description}</p>

              {features.length > 0 && (
                <div className="mb-6">
                  <h4 className="font-semibold text-gray-900 mb-3">✨ Fonctionnalités incluses :</h4>
                  <ul className="space-y-2">
                    {features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-gray-700">
                        <span className="text-yellow-500 mt-1">•</span>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-gray-700 mb-2">
                  <span className="font-semibold">Permission requise :</span> {permission}
                </p>
                <p className="text-xs text-gray-600">
                  Contactez votre administrateur pour activer cette option.
                </p>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowModal(false)}
                  className="flex-1 btn-secondary"
                >
                  Fermer
                </button>
                <button
                  onClick={() => {
                    setShowModal(false);
                    alert(`Pour activer cette option, contactez votre administrateur.\n\nPermission nécessaire : ${permission}`);
                  }}
                  className="flex-1 bg-gradient-to-r from-yellow-400 to-yellow-600 text-white font-medium py-2.5 rounded-lg hover:from-yellow-500 hover:to-yellow-700 transition-all"
                >
                  Demander l'accès
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PremiumFeatureCard;

